// React
import { ChangeEvent, useMemo } from "react";

// MUI
import {
	getGridStringOperators,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridColDef,
	GridRenderCellParams,
	GridRowId,
} from "@mui/x-data-grid-premium";

// Third Party
import moment from "moment";

// Utils
import { generateRenderingFn, getPinnedCellClassName } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { isFooterCell } from "@Common/helpers/Tables";
import { getDateInUTC, getFormattedDate } from "@dealerwave/dw-layer-date-converter";
import { notEqualsOperator } from "@Layouts/components/Tables/DataSmartTable/helpers/custom-filters";

// Components
import TypographyCell from "@Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { TypographyHeader } from "@Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";

// Types
import { Checkbox } from "@mui/material";
import { ILocation } from "@Common/interfaces/location";

// Assets
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";

// Constants
import { HEADER_CLASS_NAME } from "@Constants/tables";
import { DATE_FORMAT } from "@Constants/date";
import { NOT_APPLICABLE } from "@DataPages/constants/cit.ts";

// Assets
import { Coin } from "@DataPages/components/FniManagement/components/CIT/Coin";

type TUseAllDealsTableColumns = {
	location: ILocation;
	onRowSelection: (event: ChangeEvent<HTMLInputElement>, id: GridRowId) => void;
};

export default function useAllDealsTableColumns({ location, onRowSelection }: TUseAllDealsTableColumns) {
	const { timezone } = location ?? {};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: "_id",
				headerName: "",
				width: 1,
				filterable: false,
				sortable: false,
			},
			{
				field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
				headerName: GRID_CHECKBOX_SELECTION_COL_DEF.field,
				width: 50,
				filterable: false,
				sortable: false,
				resizable: false,
				cellClassName: getPinnedCellClassName,
				renderHeader: () => <Checkbox />,
				renderCell: (params) => {
					if (isFooterCell(params)) {
						return <p className={"!col-span-3 !text-[14px] font-montserrat footer"}>Total</p>;
					}

					return <Checkbox checked={params.value} onChange={(value) => onRowSelection(value, params.id)} />;
				},
			},
			{
				headerName: "",
				field: "funded",
				width: 40,
				align: "center",
				cellClassName: getPinnedCellClassName,
				headerClassName: HEADER_CLASS_NAME,
				type: "singleSelect",
				valueOptions: ["Funded", "Unfunded", NOT_APPLICABLE],
				valueGetter: (value) => {
					return value ? "Funded" : "Unfunded";
				},
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: (params) => {
					if (isFooterCell(params)) {
						return null;
					}

					if (params.value === NOT_APPLICABLE) {
						return <></>;
					}
					return (
						<TypographyCell component={"div"}>
							<Coin active={params.value === "Funded"} activeText="Funded" inactiveText="Unfunded" />
						</TypographyCell>
					);
				},
			},
			{
				field: "date",
				headerName: "Transaction Date",
				type: "date",
				align: "left",
				width: 100,
				cellClassName: getPinnedCellClassName,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				valueGetter: (value: Date) => value && getDateInUTC(new Date(moment.utc(value).format(DATE_FORMAT.monthAbbrDate))),
				valueFormatter: (value: Date) => getFormattedDate(value, "", DATE_FORMAT.monthAbbrDatePadded),
				renderCell: generateRenderingFn({}, { customFooterValue: " " }),
			},
			{
				field: "dealNumber",
				headerName: "Deal #",
				width: 70,
				type: "string",
				align: "right",
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getPinnedCellClassName,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				renderCell: (params) => {
					if (isFooterCell(params)) {
						return <TypographyCell params={params} className={"!text-white !font-montserrat !font-medium !text-sm"} />;
					}
					return <TypographyCell params={params} />;
				},
			},
			{
				field: "balance",
				headerName: "Balance",
				type: "number",
				width: 120,
				headerClassName: HEADER_CLASS_NAME,
				cellClassName: getPinnedCellClassName,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: (params: GridRenderCellParams<any, any, any>) => {
					if (isFooterCell(params)) {
						return (
							<TypographyCell
								params={params}
								render={"absolute"}
								hints={{ isMoney: true, noFocus: true }}
								className={"!text-white !font-montserrat !font-medium !text-sm"}
							/>
						);
					}
					return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />;
				},
			},
			{
				field: "__empty",
				headerName: "",
				sortable: false,
				resizable: false,
				filterable: false,
				minWidth: 12,
				width: 12,
				maxWidth: 12,
				headerClassName: "!z-10 !bg-white !border-none !p-0",
				cellClassName: () => "empty-row !bg-white !border-none !p-0",
				renderCell: () => <TypographyCell className={"!bg-blue"} />,
			},
			{
				field: "age",
				headerName: "Age",
				type: "number",
				align: "right",
				width: 65,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				renderCell: generateRenderingFn({}, { customFooterValue: " " }),
			},
			{
				field: "customerNumber",
				headerName: "Customer #",
				width: 100,
				align: "right",
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "customerName",
				headerName: "Customer Name",
				width: 120,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "lender",
				headerName: "Lender",
				align: "right",
				width: 130,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "stockNumber",
				headerName: "Stock #",
				align: "center",
				width: 90,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} center />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "vinNumber",
				headerName: "VIN",
				align: "right",
				width: 130,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} right />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "dealStatus",
				headerName: "Deal Status",
				align: "left",
				width: 80,
				filterOperators: [
					...getGridStringOperators(), // :contentReference[oaicite:0]{index=0}
					// then our custom “Not equal”
					notEqualsOperator,
				],
				headerClassName: HEADER_CLASS_NAME,
				valueGetter: (value) => value ?? "",
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: (params: GridRenderCellParams) => {
					if (isFooterCell(params)) {
						return " ";
					}

					if (!params.value) {
						return undefined;
					}

					return (
						<TypographyCell params={params}>
							<CircleRoundedIcon
								className={`deal-status-${params.value}`}
								sx={{
									fontSize: "12px",
									marginTop: "-3px",
									marginRight: "5px",
								}}
							/>
							{params.value}
						</TypographyCell>
					);
				},
			},
			{
				field: "amountFinanced",
				headerName: "Financed Amount",
				type: "number",
				width: 135,
				align: "right",
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: (params: GridRenderCellParams) => {
					if (isFooterCell(params)) {
						return (
							<TypographyCell
								params={params}
								render={"absolute"}
								hints={{ isMoney: true, noFocus: true }}
								className={"!text-white !font-montserrat !font-medium !text-sm"}
							/>
						);
					}

					return <TypographyCell params={params} render={"absolute"} hints={{ isMoney: true, noFocus: true }} />;
				},
			},
			{
				field: "financeEmployee",
				headerName: "F&I Manager",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "financeEmployeeId",
				headerName: "Finance Employee ID",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "salesEmployee",
				headerName: "Sales Employee",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "salesEmployeeId",
				headerName: "Sales Employee ID",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "salesManager",
				headerName: "Sales Manager",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "salesManagerEmployeeId",
				headerName: "Sales Manager ID",
				align: "left",
				width: 110,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} left />,
				renderCell: generateRenderingFn({}, {}),
			},
			{
				field: "trade",
				headerName: "Trade",
				width: 70,
				headerClassName: HEADER_CLASS_NAME,
				renderHeader: (params) => <TypographyHeader params={params} />,
				renderCell: generateRenderingFn({}, {}),
			},
		];
	}, [timezone]);

	return { columns };
}
